import { EditPencilIcon, TrashDark } from "../../../../assets";
import { SearchCollaboratorsResponse } from "../../../../service/query";
import { getErrorMessageFromCode } from "../../../../service/ValidationErrorFormatter";
import { UnitTransactionsPanel } from "../../../../templates";
import { Button, Column, ErrorBox, LoadingSpinner, ReadonlyText, Row } from "../../../../widget";
import { AssociatedProjectsPanel } from "./AssociatedProjectsPanel";
import { CompanyPanelLayout } from "./CompanyPanelLayout";
import { LinkProjectModal } from "./LinkProjectModal";
import { useCompanyDetailsModal } from "./useCompanyDetailsModal";

interface CompanyDetailsModalProps {
  show: boolean;
  onClose: () => void;
  companyUuid: string | null;
  onDeleted?: () => void;
  onEdit?: () => void;
  fetchCompanyByUuid: (companyUuid: string) => Promise<SearchCollaboratorsResponse["results"][number] | null>;
}

export const CompanyDetailsModal = ({
  show,
  onClose,
  companyUuid,
  onDeleted,
  onEdit,
  fetchCompanyByUuid,
}: CompanyDetailsModalProps): JSX.Element | null => {
  const {
    company,
    isCompanyLoading,
    companyErrorCode,
    requestCompanyRefetch,
    countryName,
    linkedOrganisationName,
    isDeleting,
    showLinkProjectModal,
    linkProjectErrors,
    isLinkProjectSaving,
    displaySource,
    setDisplaySource,
    projectOptions,
    projectUuid,
    companyRole,
    setCompanyRole,
    setProjectUuid,
    fetchProjects,
    associatedProjects,
    associatedProjectRows,
    handleDelete,
    handleOpenLinkProject,
    handleCloseLinkProject,
    handleCreateLinkProject,
    dataGridColumns,
    onAssociatedProjectRowsGridChange,
    onKanaHubAssociatedProjectRowsGridChange,
    transactionsProjectUuid,
    handleCloseTransactions,
  } = useCompanyDetailsModal({
    companyUuid,
    show,
    onClose,
    onDeleted,
    fetchCompanyByUuid,
  });

  if (!company && !isCompanyLoading && companyErrorCode) {
    return (
      <CompanyPanelLayout
        show={show}
        onClose={onClose}
        header="Company details"
        title="Company details unavailable"
        subtitle="Company details"
        sectionTitle="Company details"
        showSectionHeader={false}
      >
        <Row spacingV="m" justify="center">
          <Column span={8}>
            <ErrorBox title="Unable to load company details" text={[getErrorMessageFromCode(companyErrorCode)]} />
          </Column>
        </Row>
        <Row spacingV="m" justify="center">
          <Button text="Retry" corners="rounded" onClick={requestCompanyRefetch} />
        </Row>
      </CompanyPanelLayout>
    );
  }

  if (!company && !isCompanyLoading) return null;

  if (!company && isCompanyLoading) {
    return (
      <CompanyPanelLayout
        show={show}
        onClose={onClose}
        header="Company details"
        title="Loading company"
        subtitle="Company details"
        sectionTitle="Company details"
        showSectionHeader={false}
      >
        <Row spacingV="m" justify="center">
          <Column span={12} justify="center" align="center">
            <LoadingSpinner size="medium" type="dark" />
          </Column>
        </Row>
      </CompanyPanelLayout>
    );
  }

  if (!company) return null;

  const showAssociatedProjects = Boolean(company?.organisationUuid);

  return (
    <CompanyPanelLayout
      show={show}
      onClose={onClose}
      header="Company details"
      headerAction={
        <button
          type="button"
          className="CompanyPanelIconButton"
          onClick={handleDelete}
          aria-label="Delete company"
          disabled={isDeleting}
        >
          <TrashDark />
        </button>
      }
      title={company.name}
      subtitle="Company details"
      sectionTitle="Company details"
      sectionAction={
        onEdit ? (
          <button type="button" className="CompanyPanelIconButton" onClick={onEdit} aria-label="Edit company">
            <EditPencilIcon />
          </button>
        ) : null
      }
    >
      <Row spacingV="m">
        <Column span={6}>
          <ReadonlyText label="Name" value={company.name || company.organisationUuid} />
        </Column>
        <Column span={6}>
          <ReadonlyText label="Company type" value={company.collaboratorType} />
        </Column>
      </Row>
      <Row spacingV="m">
        <Column span={12}>
          <ReadonlyText label="Description" value={company.description} />
        </Column>
      </Row>
      <Row spacingV="m">
        <Column span={6}>
          <ReadonlyText label="Email address" value={company.email} />
        </Column>
        <Column span={6}>
          <ReadonlyText label="Phone number" value={company.phone} />
        </Column>
      </Row>
      <Row spacingV="m">
        <Column span={6}>
          <ReadonlyText label="Website" value={company.website} isExternalLink={!!company.website} />
        </Column>
        <Column span={6}>
          <ReadonlyText label="Address line 1" value={company.addressLine1} />
        </Column>
      </Row>
      <Row spacingV="m">
        <Column span={6}>
          <ReadonlyText label="Address line 2" value={company.addressLine2} />
        </Column>
        <Column span={6}>
          <ReadonlyText label="City" value={company.addressCity} />
        </Column>
      </Row>
      <Row spacingV="m">
        <Column span={6}>
          <ReadonlyText label="Postcode" value={company.addressPostcode} />
        </Column>
        <Column span={6}>
          <ReadonlyText label="County / Region / State" value={company.addressRegion} />
        </Column>
      </Row>
      <Row spacingV="m">
        <Column span={6}>
          <ReadonlyText label="CRM ID" value={company.externalReference} />
        </Column>
        <Column span={6}>
          <ReadonlyText label="Country" value={countryName} />
        </Column>
      </Row>
      <Row spacingV="m">
        <Column span={6}>
          <ReadonlyText label="Link to existing Kana developer" value={linkedOrganisationName} />
        </Column>
      </Row>
      <AssociatedProjectsPanel
        projects={associatedProjects}
        associatedProjectGridData={associatedProjectRows}
        isLinkedToOrganisation={showAssociatedProjects}
        displaySource={displaySource}
        onDisplaySourceChange={setDisplaySource}
        onLinkProject={handleOpenLinkProject}
        dataGridColumns={dataGridColumns}
        onAssociatedProjectRowsGridChange={onAssociatedProjectRowsGridChange}
        onKanaHubAssociatedProjectRowsGridChange={onKanaHubAssociatedProjectRowsGridChange}
      />
      <LinkProjectModal
        show={showLinkProjectModal}
        onClose={handleCloseLinkProject}
        projectOptions={projectOptions}
        projectUuid={projectUuid}
        companyRole={companyRole}
        setCompanyRole={setCompanyRole}
        setProjectUuid={setProjectUuid}
        fetchProjects={fetchProjects}
        errors={linkProjectErrors}
        isSaving={isLinkProjectSaving}
        onCreate={handleCreateLinkProject}
      />
      <UnitTransactionsPanel
        filter={
          transactionsProjectUuid ? { projectUuid: transactionsProjectUuid, companyUuid: company.uuid } : undefined
        }
        show={transactionsProjectUuid !== null}
        onClose={handleCloseTransactions}
        handleRefresh={requestCompanyRefetch}
      />
    </CompanyPanelLayout>
  );
};
